import React, { memo } from 'react';

import useEvent from '@lyearn/core/hooks/useEvent';
import useToggle from '@lyearn/core/hooks/useToggle';
import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Stack, Tooltip } from '@lyearn/ui';

import { SingleSelectValue, TagValues } from '../../../types';
import ActionButton from '../ActionButton';
import { TagsValueSelectorProps } from '../types';
import SingleSelectComp from './SingleSelectComp';

function SingleSelectContainer(props: TagsValueSelectorProps) {
  const { t } = useTranslation();
  const { className, tag, selectedTagValue, onDelete, readOnly, error, children, disabledMessage } =
    props;
  const [open, { toggle }] = useToggle(false);
  const singleSelectTagValue = selectedTagValue as SingleSelectValue;
  const selectedValue = singleSelectTagValue?.[0]?.label;

  const onChange = useEvent((val: TagValues) => {
    props.onChange(val);
    toggle();
  });

  const isDisabled = Boolean(disabledMessage);

  const handleClick = () => {
    if (!readOnly && !isDisabled) {
      toggle();
    }
  };

  const renderChildren = () => {
    return children ? (
      children
    ) : (
      <ActionButton
        className={className}
        defaultPlaceholder={t('Tags:Form.SelectValue')}
        disabledMessage={disabledMessage}
        error={error}
        isInFocus={open}
        readOnly={readOnly}
        selectedTagValue={selectedValue}
        tag={tag}
        onDelete={onDelete}
      />
    );
  };

  const renderContent = () => {
    if (isDisabled) {
      return (
        <Tooltip title={disabledMessage}>
          <div>{renderChildren()}</div>
        </Tooltip>
      );
    }
    return renderChildren();
  };

  if (!open) {
    return (
      <Stack className={cn('max-w-full', className)} onClick={handleClick}>
        {renderContent()}
      </Stack>
    );
  }

  return (
    <SingleSelectComp {...props} toggle={toggle} onChange={onChange}>
      {renderContent()}
    </SingleSelectComp>
  );
}

export default memo(SingleSelectContainer);
